import { homeQueryKeys } from "@kovernow/flows/home";
import { toRecoveryAction, type RecoveryAction, type RecoveryActionName } from "@kovernow/flows/recovery";
import {
  canEditOrDelete,
  canManageDocuments,
  isCollectorItem,
  isLegacyItem,
  validateCodeToAction,
  vaultQueryKeys,
  type VaultItem,
} from "@kovernow/flows/vault";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "../../core/app-context";

/**
 * The `POST /items/validate` gate before cover is bought, shared by the item
 * detail's "Choose plan" (W-6) and Choose plan's "Continue to pay" (W-10):
 * every code goes through `validateCodeToAction` and the same recovery
 * handlers, so both screens recover the same way.
 */

export type ValidateView =
  | { kind: "info"; title: string; message: string }
  | { kind: "recovery"; action: RecoveryAction; message?: string; target?: "documents" | "photos" };

/** What the item allows: edit (photos/details) and document changes. */
export function validateGateOptions(item: VaultItem, extensible: boolean) {
  const collector = isCollectorItem(item);
  const legacy = isLegacyItem(item);
  return {
    collector,
    canEdit: canEditOrDelete(item) && (legacy || collector),
    manageable: legacy && canManageDocuments(item, extensible),
  };
}

/**
 * Runs validate for `item` and calls `onProceed` on `all_good`. Any other
 * code becomes a `ValidateView`; a failed call becomes a RecoveryView.
 * `onProceed` may throw: its failure is shown the same way.
 */
export function useValidateGate(item: VaultItem | undefined, opts: { extensible?: boolean; onProceed: () => Promise<void> | void }) {
  const { vaultApi, config } = useServices();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<ValidateView | null>(null);
  const inflight = useRef(false);

  const run = async () => {
    if (!item || inflight.current) return;
    inflight.current = true;
    setBusy(true);
    setView(null);
    try {
      const r = await vaultApi.validate(item._id);
      // A validate can move the item (HK review): show the new status.
      void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.item(item._id), exact: true });
      void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.items() });
      void queryClient.invalidateQueries({ queryKey: homeQueryKeys.dashboard() });
      const { collector, canEdit, manageable } = validateGateOptions(item, opts.extensible === true);
      const outcome = validateCodeToAction(r.code, {
        canEdit,
        collector,
        region: config.region,
        canManageDocuments: manageable,
      });
      if (outcome.kind === "proceed") {
        await opts.onProceed();
      } else if (outcome.kind === "info") {
        setView({ kind: "info", title: r.title, message: r.message });
      } else {
        setView({
          kind: "recovery",
          message: r.message,
          target: outcome.target,
          action: {
            errorCode: r.code,
            action: outcome.action,
            kind: outcome.action === "contact_support" ? "nonRecoverable" : "recoverable",
            messageKey: `errors.${r.code}`,
          },
        });
      }
    } catch (err) {
      setView({ kind: "recovery", action: toRecoveryAction(err) });
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };

  return { run, busy, view, setView };
}

/**
 * Where each validate recovery leads. `verifyReturnTo` is the screen to come
 * back to after W-7/W-8; `focusDocuments` focuses the screen's own Documents
 * heading (item detail) — without it, the item detail is opened instead.
 */
export function useValidateRecoveryHandlers(
  itemId: string,
  opts: { verifyReturnTo: string; focusDocuments?: () => void },
) {
  const navigate = useNavigate();
  const editRoute = `/vault/${encodeURIComponent(itemId)}/edit`;
  return (target?: "documents" | "photos"): Partial<Record<RecoveryActionName, () => void>> => ({
    upload_documents: () => {
      if (target === "photos") navigate(`${editRoute}?step=photos`);
      else if (opts.focusDocuments) opts.focusDocuments();
      else navigate(`/vault/${encodeURIComponent(itemId)}`);
    },
    update_item_details: () => navigate(editRoute),
    verify_identity: () => navigate(`/account/verify?returnTo=${encodeURIComponent(opts.verifyReturnTo)}`),
  });
}
